import { useState } from 'react';
import {
  CategoryUnionType,
  mapCompanyTagType,
  mapCompanyType,
  useFindCompanySuspense,
  useFindCompanyTagsSuspense,
} from '@api';
import {
  AddActionIcon,
  AddPersonFormSchemaType,
  AddPersonModal,
  CompanyLinkForm,
  CompanyLinkFormValuesType,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  useCreateCompanyLink,
  useCreatePerson,
  useDeleteCompanyLink,
  useFormatAddress,
  useUpdateCompanyLink,
} from '@common';
import { Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/companies/$id.employees';
import { CompanyEmployeesTable } from './components';
import { useCompanyEmployeesData } from './hooks';

export const CompanyEmployees: React.FC = () => {
  const { t } = useTranslation('companyDetailsEmployees');
  const { id: companyId } = Route.useParams();
  const { data: companyName } = useFindCompanySuspense(companyId, {
    query: { select: (data) => mapCompanyType(data.data).name },
  });
  const { data: companyTags } = useFindCompanyTagsSuspense({
    query: {
      select: (data) => data.data.map(mapCompanyTagType),
    },
  });
  const { sites, companyLinkCategories, personsForSelect } =
    useCompanyEmployeesData();
  const { handleDeleteCompanyLink } = useDeleteCompanyLink();
  const { updateCompanyLink } = useUpdateCompanyLink();

  const { formatAddressSingleLine } = useFormatAddress();
  const [creatingForSiteId, setCreatingForSiteId] = useState<string | null>(
    null
  );
  const [
    addPersonModalOpened,
    { open: openAddPersonModal, close: closeAddPersonModal },
  ] = useDisclosure(false);
  const [preselectPersonId, setPreselectPersonId] = useState<string | null>(
    null
  );

  const { createCompanyLink } = useCreateCompanyLink();

  const { createPerson } = useCreatePerson();

  const handleAddPersonSubmit = async (
    data: AddPersonFormSchemaType
  ): Promise<void> => {
    const response = await createPerson(data);
    setPreselectPersonId(response.data.id);
  };

  const handleCreateCompanyEmployee = async (
    formValues: CompanyLinkFormValuesType
  ) => {
    if (!creatingForSiteId) return;

    await createCompanyLink({
      ...formValues,
      siteId: creatingForSiteId,
      categories: formValues.categories as CategoryUnionType[],
    });
    setCreatingForSiteId(null);
    setPreselectPersonId(null);
  };

  return (
    <Stack gap="md" data-testid="company-employees">
      {sites.map((site) => (
        <TouringCard key={site.id} data-testid="company-employees-site-card">
          <TouringCardHeader>
            <TouringCardTitle>
              {formatAddressSingleLine(site.address) ?? ''}
            </TouringCardTitle>
            <TouringCardActions>
              <AddActionIcon onClick={() => setCreatingForSiteId(site.id)} />
            </TouringCardActions>
          </TouringCardHeader>
          <TouringCardSection>
            <CompanyEmployeesTable
              companyId={companyId}
              companyName={companyName}
              companyTags={companyTags}
              siteId={site.id}
              handleDeleteCompanyLink={handleDeleteCompanyLink}
              updateCompanyLink={updateCompanyLink}
            />
          </TouringCardSection>
        </TouringCard>
      ))}
      <Modal
        opened={creatingForSiteId !== null}
        onClose={() => {
          setCreatingForSiteId(null);
          setPreselectPersonId(null);
        }}
        title={t('MODAL.CREATE_EMPLOYEE_TITLE')}
        size="lg"
      >
        {creatingForSiteId && (
          <CompanyLinkForm.Root
            key={`create-employee-${creatingForSiteId}`}
            handleSubmit={handleCreateCompanyEmployee}
            onCancel={() => {
              setCreatingForSiteId(null);
              setPreselectPersonId(null);
            }}
          >
            <CompanyLinkForm.BusinessFields.Root>
              <CompanyLinkForm.BusinessFields.PersonSelect
                persons={personsForSelect}
                onAddPerson={openAddPersonModal}
                preselectPersonId={preselectPersonId}
              />
              <CompanyLinkForm.BusinessFields.JobTitle />
              <CompanyLinkForm.BusinessFields.BuyerId />
            </CompanyLinkForm.BusinessFields.Root>
            <CompanyLinkForm.ContactFields />
            <CompanyLinkForm.Categories
              companyLinkCategories={companyLinkCategories}
            />
            <CompanyLinkForm.ExcludeFromConfirmationMail />
          </CompanyLinkForm.Root>
        )}
      </Modal>
      <AddPersonModal
        opened={addPersonModalOpened}
        onClose={closeAddPersonModal}
        onSubmit={handleAddPersonSubmit}
      />
    </Stack>
  );
};
